<template>
  <LoadingCard :loading="loading && !data" class="px-6 py-4">
    <div class="panel-metric-header mb-3">
      <div class="panel-metric-header__title">
        <h3>{{ __('Main points by status') }}</h3>
      </div>

      <SelectControl
        class="panel-metric-header__select"
        variant="compact"
        size="xs"
        :bordered="false"
        :options="ranges"
        :selected="range"
        :aria-label="__('Select Ranges')"
        @change="select"
      />
    </div>

    <p v-if="error" class="text-sm text-red-500">{{ error }}</p>

    <div v-else class="min-h-[90px]">
      <div class="max-h-[110px] overflow-hidden overflow-y-auto">
        <ul>
          <li v-for="item in items" :key="item.key" class="text-xs leading-normal">
            <span
              class="mr-2 inline-block h-2 w-2 rounded-full"
              :style="{ backgroundColor: item.color }"
            />{{ item.label }} ({{ item.count }} - {{ item.percentage }}%)
          </li>
          <li v-if="!items.length" class="text-xs text-gray-400">{{ __('No points in this range.') }}</li>
        </ul>
      </div>

      <div
        ref="chart"
        class="vertical-center ct-chart right-[20px] mr-4 rounded-b-lg"
        :class="{ invisible: total <= 0 }"
        style="width: 90px; height: 90px; bottom: 30px; top: calc(50% + 15px)"
      />
    </div>
  </LoadingCard>
</template>

<script>
import Chartist from 'chartist'
import 'chartist/dist/chartist.min.css'

export default {
  props: ['card'],

  data() {
    return {
      range: 'today',
      data: null,
      loading: true,
      error: null,
      chartist: null,
      colors: {
        watching: '#3b82f6',
        opened: '#eab308',
        manual_opened: '#f59e0b',
        suspended: '#8b5cf6',
        held_session: '#d97706',
        profited: '#22c55e',
        stopped: '#ef4444',
        closed_risk_free: '#14b8a6',
        manual_closed: '#f97316',
        cancelled: '#a1a1aa',
      },
    }
  },

  computed: {
    ranges() {
      return [
        { label: this.__('Today'), value: 'today' },
        { label: this.__('Yesterday'), value: 'yesterday' },
        { label: this.__('Last 7 days'), value: '7d' },
        { label: this.__('Last 30 days'), value: '30d' },
      ]
    },

    total() {
      return (this.data?.statuses || []).reduce((sum, status) => sum + status.count, 0)
    },

    items() {
      return (this.data?.statuses || []).map(status => ({
        key: status.key,
        label: status.label,
        count: status.count,
        color: this.colors[status.key] || '#a1a1aa',
        percentage: this.total > 0 ? Math.round((status.count / this.total) * 100) : 0,
      }))
    },
  },

  watch: {
    items() {
      this.renderChart()
    },
  },

  created() {
    this.load()
  },

  mounted() {
    this.chartist = new Chartist.Pie(this.$refs.chart, this.chartData(), {
      donut: true,
      donutWidth: 10,
      donutSolid: true,
      startAngle: 270,
      showLabel: false,
    })

    this.chartist.on('draw', context => {
      if (context.type === 'slice') {
        context.element.attr({ style: `fill: ${context.meta.color} !important` })
      }
    })
  },

  methods: {
    chartData() {
      return {
        labels: this.items.map(item => item.label),
        series: this.items.map(item => ({ value: item.count, meta: { color: item.color } })),
      }
    },

    renderChart() {
      this.chartist?.update(this.chartData())
    },

    select(event) {
      this.range = event?.target?.value || event
      this.load()
    },

    async load() {
      this.loading = true
      this.error = null
      try {
        const { data } = await Nova.request().get('/nova-vendor/coins/main-point-insights', {
          params: { range: this.range },
        })
        this.data = data
      } catch (e) {
        this.error = this.__('Could not load the insights.')
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
